<script setup lang="ts">
import SvgIcon from "~/components/Common/SvgIcon.vue";
import Tag from "~/components/Common/Tag.vue";
import { useFormats } from "~/composables/utils/useFormat";
import Tooltip from "~/components/Common/Tooltip.vue";
import TransitionHeight from "~/components/Common/TransitionHeight.vue";
import TrainingsAttendees from "./TrainingsAttendees.vue";
import { useLearningPathTrainingsStore } from "~/store/learning-path/learning-path-trainings.store";
import { fetchWithStateHandling } from "~/composables/utils/useStateManager";
import { LearningPathTrainingStatusEnum } from "~/utils/enums/learning-path.enums";
import { TrainingFormatTags } from "~/utils/options/session-items.options";
import type { ILearningPathTrainingInscriptionCard } from "~/utils/interfaces/learning-path-interfaces";

const props = defineProps<{
  index: number;
  training: ILearningPathTrainingInscriptionCard;
  disabled: boolean;
  isOpen: boolean;
}>();
const emit = defineEmits<{
  toggle: [learningPathTrainingId: string];
}>();

const formats = useFormats();
// The format lives on the training itself, so an LMS training is no longer forced to read "eLearning".
const formatTag = computed(() => (props.training.format ? TrainingFormatTags[props.training.format] : null));
const cardRef = ref<HTMLElement | null>(null);
const { fetchLearningPathTrainingsAttendeesFullDatas } = useLearningPathTrainingsStore();
const { learningPathTrainingsAttendeesFullDatas } = storeToRefs(useLearningPathTrainingsStore());
function toggleOpen() {
  emit("toggle", props.training.learningPathTrainingId);
}

const isLoadingRef = ref(false);
const isErrorRef = ref(false);
const isContentReady = ref(false);

async function loadDatas() {
  const { isLoading, isError } = await fetchWithStateHandling(async () => {
    await fetchLearningPathTrainingsAttendeesFullDatas(props.training.learningPathTrainingId);
  });

  isLoadingRef.value = isLoading.value;
  isErrorRef.value = isError.value;
}

// Load data first, then show content to avoid flicker
watch(
  () => props.isOpen,
  async (newValue) => {
    if (newValue && cardRef.value) {
      // Load data before showing content
      await loadDatas();
      isContentReady.value = true;

      await nextTick();
      setTimeout(() => {
        cardRef.value?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, 200);
    } else {
      isContentReady.value = false;
    }
  },
);
</script>
<template>
  <div ref="cardRef" class="flex items-center gap-4 w-full">
    <div class="container-card">
      <div class="flex flex-col w-full">
        <div
          class="flex flex-row gap-[8px] w-full justify-between px-6 py-4 cursor-pointer"
          @click="
            () =>
              (training.status === LearningPathTrainingStatusEnum.ACTIVE ||
                training.status === LearningPathTrainingStatusEnum.ERROR) &&
              toggleOpen()
          "
        >
          <div class="flex flex-col gap-[8px]">
            <div class="flex gap-[16px] items-center">
              <div class="training_card__img_container">
                <img v-if="training.tpLogo" class="training_card__img" :src="training.tpLogo" alt="logo partenaire" />
                <div
                  v-else
                  class="w-[64px] h-[64px] bg-lightest-grey-color flex items-center justify-center rounded-[16px]"
                >
                  <SvgIcon name="graduation_cap" class="h-[21px] w-[31px] text-medium-grey-color" />
                </div>
              </div>
              <div class="flex flex-col text-start flex-1 min-w-0">
                <span class="body_med text-primary-color trainings_text">{{ training.title }}</span>
                <span class="body_regular text-tertiary-color trainings_text">{{ training.trainingPartnerName }}</span>
              </div>
            </div>

            <!-- Tags Section -->
            <div class="flex gap-1 items-start flex-wrap">
              <Tag
                v-if="training.durationHours && Number.isFinite(training.durationHours)"
                :tag="{
                  type: 'light_grey',
                  value: `${formats.formatTo2Decimals(training.durationHours)}h`,
                  logo: 'time_normal',
                }"
              >
              </Tag>
              <Tag v-if="formatTag" :tag="formatTag"> </Tag>
            </div>
          </div>
          <!-- Menu Button -->
          <div
            v-if="training.status === LearningPathTrainingStatusEnum.ACTIVE"
            class="flex gap-2 justify-center items-baseline mt-[20px]"
          >
            <div class="flex gap-1">
              <Tag
                v-if="
                  !training.isOnDemand &&
                  training.nbSessions &&
                  Number.isFinite(training.nbSessions) &&
                  training.nbSessions > 0
                "
                :tag="{
                  type: 'grey',
                  value: `${training.nbSessions} session(s) disponible(s)`,
                  logo: 'date',
                }"
              ></Tag>
              <Tag
                v-else-if="!training.isOnDemand && training.nbSessions && Number.isFinite(training.nbSessions)"
                :tag="{
                  type: 'light_red',
                  value: `${training.nbSessions} session à inscrire`,
                  logo: 'date',
                }"
              ></Tag>
              <Tag
                v-else-if="!training.isOnDemand"
                :tag="{
                  type: 'grey',
                  value: `Aucune session disponible`,
                  logo: 'date',
                }"
              ></Tag>
              <Tag
                v-if="
                  training.nbAttendeesToBook &&
                  Number.isFinite(training.nbAttendeesToBook) &&
                  training.nbAttendeesToBook > 0
                "
                :tag="{
                  type: 'light_orange',
                  value: `${training.nbAttendeesToBook} participant(s) à inscrire`,
                  logo: 'indiv',
                }"
              ></Tag>
              <Tag
                v-else-if="training.nbAttendeesToBook && Number.isFinite(training.nbAttendeesToBook)"
                :tag="{
                  type: 'grey',
                  value: `${training.nbAttendeesToBook} participant à inscrire`,
                  logo: 'indiv',
                }"
              ></Tag>
            </div>

            <Tooltip :position="'top-left'" append-to-body minified>
              <template #img>
                <SvgIcon name="see" class="icon cursor-pointer w-4 h-4 text-primary-blue-color"> </SvgIcon>
              </template>
              <template #content>
                <span class="body_regular"> Voir le détail </span>
              </template>
            </Tooltip>
          </div>
          <!-- Registration Loading Status (Static for testing) -->
          <div v-if="training.status !== LearningPathTrainingStatusEnum.ACTIVE">
            <div
              class="registration-loading-status"
              :class="{ error: training.status === LearningPathTrainingStatusEnum.ERROR }"
            >
              <SvgIcon
                v-if="training.status === LearningPathTrainingStatusEnum.ERROR"
                name="warning_error"
                class="w-4 h-4 text-primary-red-color"
              />
              <SvgIcon v-else name="loader" class="w-4 h-4 spin-slow text-secondary-color" />
              <span
                v-if="training.status === LearningPathTrainingStatusEnum.CHECKING_EXISTING_BOOKINGS"
                class="status-text"
              >
                Vérification des inscriptions existantes...
              </span>
              <span
                v-else-if="training.status === LearningPathTrainingStatusEnum.CREATING_BOOKINGS"
                class="status-text"
              >
                Inscription des participants en cours...
              </span>
              <span
                v-else-if="training.status === LearningPathTrainingStatusEnum.ERROR"
                class="body_regular text-primary-red-color"
              >
                Erreur lors de l'inscription des participants...
              </span>
            </div>
          </div>
        </div>

        <!-- Expanded Content Section -->
        <TransitionHeight
          v-if="
            training.status === LearningPathTrainingStatusEnum.ACTIVE ||
            training.status === LearningPathTrainingStatusEnum.ERROR
          "
          :duration="300"
        >
          <div
            v-if="isContentReady"
            class="px-6 pb-6 pt-4"
            :class="{
              'max-h-[200px]': !isLoadingRef && !isErrorRef && !learningPathTrainingsAttendeesFullDatas?.data?.length,
            }"
          >
            <TrainingsAttendees
              :is-loading="isLoadingRef"
              :is-error="isErrorRef"
              :learning-path-training-id="training.learningPathTrainingId"
            />
          </div>
        </TransitionHeight>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use "assets/scss/texts";

.container-card {
  @apply flex w-full;
  scrollbar-gutter: stable;
  border: 1px solid var(--grey-lightest);
  background: var(--white-main);
  border-radius: 8px;
  min-height: 130px;
  box-shadow: var(--shadow-soft);

  &:hover {
    box-shadow: var(--shadow-medium);
  }
}

.drag-handle {
  @apply cursor-grab;
}

.trainings_text {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.training_card__img_container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  min-height: 64px;
  min-width: 64px;
}

.menu-button {
  @apply w-6 h-6 rounded flex-shrink-0 cursor-pointer flex items-center justify-center;
  transition: background-color 0.2s;

  &:hover {
    background: var(--grey-lightest);
  }
}

.registration-loading-status {
  @apply flex items-center gap-2 bg-lightest-grey-color h-fit p-2 rounded-lg;

  &.error {
    @apply border border-primary-red-color;
  }
}

.status-text {
  @apply body_regular text-secondary-color;
}

.spin-slow {
  animation: spin 1.5s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}
</style>
